import { AdminShell } from '../components/AdminShell';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { useAdminMetrics } from '../useAdminMetrics';
import { cn } from '@/lib/cn';

interface SafetyMetrics {
  generated_at: string;
  active_users_24h: number;
  reports_open_24h: number;
  reports_resolved_24h: number;
  reports_total_24h: number;
  blocks_24h: number;
  conversations_started_24h: number;
  open_reports_aging: { lt_1h: number; lt_24h: number; lt_7d: number; gt_7d: number };
  verification_funnel: { started: number; pending: number; verified: number; rejected: number } | null;
}

const fmt = (n: number | undefined | null) =>
  typeof n === 'number' ? n.toLocaleString() : '—';

interface CardProps {
  label: string;
  value: string;
  sub?: string;
  tone?: 'default' | 'warn';
}

const Card = ({ label, value, sub, tone = 'default' }: CardProps) => (
  <div
    className={cn(
      'border p-5',
      tone === 'warn'
        ? 'border-pink/40 bg-pink/[0.04]'
        : 'border-white/[0.08] bg-charcoal/40',
    )}
  >
    <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
      {label}
    </p>
    <p className="mt-2 font-display text-3xl font-black uppercase tracking-display text-text-primary">
      {value}
    </p>
    {sub && <p className="mt-2 text-[11px] text-text-secondary">{sub}</p>}
  </div>
);

export const AdminSafetyPage = () => {
  const { data, loading, error } = useAdminMetrics<SafetyMetrics>('safety');
  const m = data;

  return (
    <AdminShell eyebrow="Moderation · safety">
      <div className="px-5 py-7 md:px-12 md:py-10">
        <SectionMarker className="mb-3">Last 24 hours</SectionMarker>
        <h1 className="font-display text-3xl font-black uppercase tracking-display text-text-primary md:text-4xl">
          The pulse of the room<span className="text-pink">.</span>
        </h1>
        {m?.generated_at && (
          <p className="mt-2 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            Updated {new Date(m.generated_at).toLocaleString()}
          </p>
        )}

        {error && (
          <p className="mt-4 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
            — {error}
          </p>
        )}

        {loading && !m ? (
          <p className="mt-6 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            Loading<span className="text-pink">.</span>
          </p>
        ) : (
          <>
            <div className="mt-8 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <Card label="Active users · 24h" value={fmt(m?.active_users_24h)} />
              <Card label="New conversations · 24h" value={fmt(m?.conversations_started_24h)} />
              <Card label="Blocks · 24h" value={fmt(m?.blocks_24h)} />
              <Card
                label="Open reports · 24h"
                value={fmt(m?.reports_open_24h)}
                tone={(m?.reports_open_24h ?? 0) > 0 ? 'warn' : 'default'}
                sub={`${fmt(m?.reports_resolved_24h)} resolved of ${fmt(m?.reports_total_24h)} total`}
              />
            </div>

            <SectionMarker className="mb-3 mt-10">Open reports · aging</SectionMarker>
            <div className="grid gap-3 md:grid-cols-4">
              <Card label="< 1 hour"  value={fmt(m?.open_reports_aging.lt_1h)} />
              <Card label="< 24 hours" value={fmt(m?.open_reports_aging.lt_24h)} />
              <Card label="< 7 days"  value={fmt(m?.open_reports_aging.lt_7d)} />
              <Card
                label="> 7 days"
                value={fmt(m?.open_reports_aging.gt_7d)}
                tone={(m?.open_reports_aging.gt_7d ?? 0) > 0 ? 'warn' : 'default'}
                sub="SLA breach"
              />
            </div>

            {m?.verification_funnel && (
              <>
                <SectionMarker className="mb-3 mt-10">Verification funnel</SectionMarker>
                <div className="grid gap-3 md:grid-cols-4">
                  <Card label="Started"  value={fmt(m.verification_funnel.started)} />
                  <Card label="Pending"  value={fmt(m.verification_funnel.pending)} />
                  <Card label="Verified" value={fmt(m.verification_funnel.verified)} />
                  <Card label="Rejected" value={fmt(m.verification_funnel.rejected)} />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </AdminShell>
  );
};
